import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { EVENTS, subscribe } from '@sendible/event-manager';
import { shallowEqual } from '@sendible/common';
import { BridgeContextType, SharedStateType } from '@sendible/shared-state-bridge';

interface BridgeContextProviderProps {
  initialState: SharedStateType;
  children: ReactNode;
}

type Listener = () => void;

type BridgeStore = {
  getSnapshot: () => SharedStateType;
  setState: React.Dispatch<React.SetStateAction<SharedStateType>>;
  subscribe: (listener: Listener) => () => void;
};

const createBridgeStore = (initialState: SharedStateType): BridgeStore => {
  let state = initialState;
  const listeners = new Set<Listener>();

  const setState: BridgeStore['setState'] = (next) => {
    const resolved = typeof next === 'function' ? (next as (prev: SharedStateType) => SharedStateType)(state) : next;

    if (Object.is(resolved, state)) return;

    state = resolved;
    listeners.forEach((listener) => listener());
  };

  return {
    getSnapshot: () => state,
    setState,
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
};

const BridgeStoreContext = createContext<BridgeStore>(undefined!);

const BridgeContextProvider: FC<BridgeContextProviderProps> = (props) => {
  const { children, initialState } = props;
  const [store] = useState(() => createBridgeStore(initialState));

  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.SHARED_STATE_CHANGED, () => {
      store.setState(window.sharedState || ({} as SharedStateType));
    });

    return unsubscribe;
  }, [store]);

  return <BridgeStoreContext.Provider value={store}>{children}</BridgeStoreContext.Provider>;
};

const useBridgeStore = (): BridgeStore => {
  const store = useContext(BridgeStoreContext);

  if (!store) {
    throw new Error('getContext must be used within BridgeContext');
  }

  return store;
};

const useBridgeContext = (): BridgeContextType => {
  const store = useBridgeStore();
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  return useMemo<BridgeContextType>(() => [state, store.setState], [state, store.setState]);
};

const useBridgeSelector = <T,>(selector: (state: SharedStateType) => T, isEqual: (a: T, b: T) => boolean = Object.is): T => {
  const store = useBridgeStore();
  const cacheRef = useRef<{ source: SharedStateType; value: T } | null>(null);

  const getSelection = useCallback(() => {
    const source = store.getSnapshot();
    const cache = cacheRef.current;

    if (cache && Object.is(cache.source, source)) return cache.value;

    const next = selector(source);
    const value = cache && isEqual(cache.value, next) ? cache.value : next;

    cacheRef.current = { source, value };

    return value;
  }, [store, selector, isEqual]);

  return useSyncExternalStore(store.subscribe, getSelection, getSelection);
};

const selectUserSnapshot = (state: SharedStateType): SharedStateType['user'] => ({ ...state.user });
const useBridgeUser = (): SharedStateType['user'] => useBridgeSelector(selectUserSnapshot, shallowEqual);

export { BridgeContextProvider, useBridgeContext, useBridgeSelector, useBridgeUser };
